import { useState, useEffect } from "react";
import DateInput from "../../DateInput";
import { format } from "date-fns";
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  CircularProgress,
} from "@mui/material";

import useSWRMutation from "swr/mutation";

import { AccountReference } from "../input/AccountReference";
import { Postcode } from "../input/Postcode";
import { Password, PasswordCriteria } from "../input/Password";

import { useUtils } from "../../../context/UtilsProvider";
import { useAuth } from "../../../context/AuthProvider";
import { useClient } from "../../../context/ClientProvider";


export default function RegOtpRegistrationCard() {

  const {
    formState,
    setFormState,
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes
  } = useUtils();

  const {
    postSignUp,
    setAuthState,
    authStates
  } = useAuth();

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  
  const { trigger, isMutating } = useSWRMutation("/auth/signup", postSignUp);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setButtonDisabled(true);
    try {
      console.log("post signup auth request: ", formState);
      const date = new Date(formState.dateOfBirth);
      const formattedDate = format(date, "yyyy-MM-dd");
      const response = await trigger({
        ...formState,
        username: formState.accountReference,
        password: formState.password,
        userAttributes: {},
        validationData: {
          dateOfBirth: formattedDate,
          postcode: formState.postcode.toUpperCase()
        }
      });
      createAlert("Registration successful. Please login.", alertSeverityTypes.success);
      console.info("post signup auth response: ", response);
      setAuthState(authStates.signedOut);
    } catch (error) {
      const message = "Hmm... something's not right. Please try again";
      console.error("post signup auth error", error);
      setFormError(message);
      setButtonDisabled(true);
    }
  };

  useEffect(() => {
    console.log("***** Form State Change *****: ", formState);
    if (
      formState.accountReference &&
      formState.dateOfBirth &&
      formState.password
    ) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [formState]);

  const signIn = () => {
    setAuthState(authStates.signedOut);
    setFormError(null);
  }

  return clientConfig && (
    <Card
      sx={{
        maxWidth: { md: "80%" },
        m: "0px auto",
        borderRadius: "0",
        position: "relative",
        bottom: { xs: "auto", md: "300px" },
        boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
      }}
    >
      <Container sx={{ mt: "25px" }}>
        <Typography
          component='h1'
          variant='h5'
          sx={{ mb: 1, fontWeight: "bold" }}
        >
          {clientConfig.layout.auth.registration.headerText}
        </Typography>
        <Typography component='h2' variant='p'>
          {clientConfig.layout.auth.registration.subHeaderText}
        </Typography>
      </Container>

      <Box
        component='form'
        onSubmit={handleSubmit}
        noValidate
        sx={{ margin: "0px" }}
      >
        <Stack
          sx={{ px: 1, pt: 1, pb: 3 }}
          direction={{
            xs: "column",
            md: "row",
          }}
        >
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Account reference input */}
            <Box id="accountReference">
              <AccountReference
                label="Account number"
                inputLabel="Enter your account number"
                formState={formState}
                setFormState={setFormState}
                setFormError={setFormError}
              />
            </Box>
            {/* End of account reference input */}

            {/* Postcode input */}
            <Box
              id="postcode"
              sx={{ mt: 2 }}
            >
              <Postcode
                label="Postcode"
                inputLabel="Enter your postcode"
                formState={formState}
                setFormState={setFormState}
                setFormError={setFormError}
              />
            </Box>
            {/* End of postcode input */}

            {/* Date of birth input */}
            <Box
              id="dateOfBirth"
              sx={{ mt: 2 }}
            >
              <Typography
                //sx={{ fontWeight: "bold", mt: { xs: 4, md: 2 } }}
                sx={{ fontWeight: "bold", mt: { xs: 4, md: 2 } }}
                component='label'
                htmlFor='dateOfBirth'
              >
                Date of birth
              </Typography>
              <DateInput
                value={formState.dateOfBirth}
                sx={{ display: "block", mt: 1, width: "100%" }}
                onChange={(value) => {
                  setFormState({ ...formState, dateOfBirth: value });
                }}
                setButtonDisabled={setButtonDisabled}
              />
            </Box>
            {/* End of date of birth input */}

          </CardContent>

          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Password input */}
            <Box id="password">
              <Password
                label="Password"
                inputLabel="Enter your password"
                formState={formState}
                setFormState={setFormState}
                setFormError={setFormError}
              />
            </Box>
            {/* End of password input */}
            
            <Box sx={{ mt: 4 }}>
              <PasswordCriteria />
            </Box>

          </CardContent>
        </Stack>

        {/* Button section */}
        <Container sx={{ textAlign: { xs: "center", md: "left" } }}>
          <Button
            type='submit'
            variant='contained'
            sx={{
              mb: 2,
              marginLeft: "1%",
              borderRadius: clientConfig.layout.buttonBorderRadius,
              width: { xs: "60%", md: "15%" },
              backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
              color: clientConfig.layout.primaryButtonColour,
              "&:hover": {
                backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                color: clientConfig.layout.hoverPrimaryButtonColour,
              }
            }}
            disabled={buttonDisabled}
          >
            Complete
          </Button>
          <Button
              variant="outlined"
              sx={{
                mb: 2,
                marginLeft: "1%",
                borderRadius: clientConfig.layout.buttonBorderRadius,
                width: { xs: "60%", md: "15%" },
                backgroundColor: clientConfig.layout.secondaryButtonBackgroundColour,
                color: clientConfig.layout.secondaryButtonColour,
                "&:hover": {
                  backgroundColor: clientConfig.layout.hoverSecondaryButtonBackgroundColour,
                  color: clientConfig.layout.hoverSecondaryButtonColour,
                },
              }}
              onClick={signIn}
          >
            Back
          </Button>
        </Container>
        {/* End of button section */}

        {/* Error message and loading section */}
        <Box sx={{ position: "relative", mb: 3, mt: 3 }}>
          {isMutating && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
          <Typography
            sx={{ ml: 1, display: "inline-block", mb: { xs: 2 } }}
            color='error'
          >
            {formError && `* ${formError}`}
          </Typography>
        </Box>
        {/* End of error message and loading section */}

      </Box>
    </Card>
  );
};